import { ROUTES } from "@/routes";
import { primaryColor } from "@/theme";
import { Chip, List, ListItem, ListItemIcon, ListItemText, Paper, Divider, Typography, Box, ListItemButton, Collapse, Avatar } from "@mui/material";
import { Link } from "react-router-dom"; // Import Link from React Router
import ListItemButtonSelected from "./ListItemButtonSelected";
import IconAppartment from "@mui/icons-material/Apartment";
import LayersIcon from "@mui/icons-material/Layers";
import DashboardIcon from "@mui/icons-material/Dashboard";
import AddIcon from "@mui/icons-material/Add";
import SettingsIcon from "@mui/icons-material/Settings";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useDataQueueCountQuery } from "@/helpers/getDataQueueCount";
import { useSelector } from "react-redux";
import userSelector from "@/store/selectors/userSelector";
import { useGetBuildingsExtended } from "@/helpers/getBuildingsExtended";
import SidebarIcon from "./SidebarIcon";
import backend from "@/helpers/backend";
import { useState } from "react";
import ElectricMeterIcon from '@mui/icons-material/ElectricMeter';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import { useGetActiveMunicipality } from "@/helpers/getMunicipality";
import { useGetBuildingGroups } from "@/helpers/getBuildingGroups";
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { IconButton } from "@mui/material";

const getBuildingInitials = (name: string) => {
  if (!name) return "";
  const words = name.split(' ').filter(w => w.length > 0);
  return words.slice(0, 3).map(w => w[0]).join('');
};

export const SidebarClientButtons = ({ isCollapsed }: { isCollapsed?: boolean }) => {
  const user = useSelector(userSelector);
  const { data } = useGetBuildingsExtended()
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [ungroupedExpanded, setUngroupedExpanded] = useState(true);
  const municipalityId = useGetActiveMunicipality();

  const { data: groups } = useGetBuildingGroups();

  const groupedBuildingIds = new Set(
    (groups ?? []).flatMap((group) => (group.buildings ?? []).map((building) => building.id))
  );
  const buildingOrderMap = new Map((data ?? []).map((building, index) => [building.id, index]));
  const ungroupedBuildings = (data ?? []).filter((building) => !groupedBuildingIds.has(building.id));
  const hasAnyCollectionPoint = (data ?? []).some((building) => (building.collectionPoints?.length ?? 0) > 0);

  const { data: dataQueueCount } = useDataQueueCountQuery({ enabled: !!user?.name && !!user?.verifiedByAdmin && !!user?.verifiedEmail });

  return (
    <>
      <ListItemButtonSelected component={Link} to={ROUTES.DASHBOARD} isCollapsed={isCollapsed}>
        <ListItemIcon>
          <SidebarIcon component={DashboardIcon} />
        </ListItemIcon>
        <ListItemText primary="Přehled" />
      </ListItemButtonSelected>

      <ListItemButtonSelected component={Link} to={ROUTES.CLIENT_SETTINGS} isCollapsed={isCollapsed}>
        <ListItemIcon>
          <SidebarIcon component={SettingsIcon} />
        </ListItemIcon>
        <ListItemText primary="Nastavení" />
      </ListItemButtonSelected>

      <ListItemButtonSelected component={Link} to={ROUTES.CLIENT_SUBSCRIPTION} isCollapsed={isCollapsed}>
        <ListItemIcon>
          <SidebarIcon component={PaymentsOutlinedIcon} />
        </ListItemIcon>
        <ListItemText primary="Předplatné" />
      </ListItemButtonSelected>

      <ListItemButtonSelected component={Link} to={ROUTES.CLIENT_BUILDING_GROUPS} isCollapsed={isCollapsed}>
        <ListItemIcon>
          <SidebarIcon component={LayersIcon} />
        </ListItemIcon>
        <ListItemText primary="Skupiny objektů" />
      </ListItemButtonSelected>

      {/* <ListItemButtonSelected sx={{ pl: 2 }} component={Link} to={ROUTES.CLIENT_UPLOAD_INVOICE} isCollapsed={isCollapsed}>
        <ListItemIcon>
          <SidebarIcon component={DescriptionIcon} />
        </ListItemIcon>
        <ListItemText primary="Nahrát vyúčtování" />
      </ListItemButtonSelected> */}

      {hasAnyCollectionPoint && (
        <ListItemButtonSelected component={Link} to={ROUTES.CLIENT_UPLOAD_CONSUMPTION} isCollapsed={isCollapsed}>
          <ListItemIcon>
            <SidebarIcon component={ElectricMeterIcon} />
          </ListItemIcon>
          <ListItemText primary="Nahrát spotřebu" />
        </ListItemButtonSelected>
      )}


      <Box sx={{ mt: 2 }}>
        <ListItem>
          <ListItemIcon>
            <SidebarIcon component={IconAppartment} />
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Objekty
              </Typography>
            }
          />
        </ListItem>
        <Divider sx={{ mx: 2, my: 1 }} />
      </Box>

      <List disablePadding>
        <ListItemButtonSelected component={Link} to={ROUTES.CLIENT_ADD_BUILDING} urlLevel={3} isCollapsed={isCollapsed}>
          <ListItemIcon>
            <SidebarIcon component={AddIcon} />
          </ListItemIcon>
          <ListItemText primary="Přidat objekt" />
        </ListItemButtonSelected>
        {(groups ?? []).map((group) => {
          const buildingsInGroup = [...(group.buildings ?? [])].sort((a, b) => {
            const orderA = buildingOrderMap.get(a.id) ?? Number.MAX_SAFE_INTEGER;
            const orderB = buildingOrderMap.get(b.id) ?? Number.MAX_SAFE_INTEGER;
            return orderA - orderB;
          });
          if (buildingsInGroup.length === 0) return null;
          const isExpanded = expandedGroups[group.id] ?? true;
          return (
            <Box key={group.id}>
              <Divider sx={{ mx: 2, my: 0.5, opacity: 0.5 }} />
              <ListItem disablePadding sx={{ px: isCollapsed ? 0 : 1, py: 0.25 }}>
                <ListItemButton
                  className="sidebar-item-level-2"
                  sx={{
                    pr: 1,
                    borderRadius: '0 20px 20px 0',
                    '&:hover': {
                      backgroundColor: primaryColor,
                      color: 'white',
                      '& .MuiListItemIcon-root': {
                        color: 'white !important',
                      },
                      '& .MuiSvgIcon-root': {
                        color: 'white !important',
                      },
                      '& .MuiListItemText-primary': {
                        color: 'white',
                      },
                      '& .MuiListItemText-secondary': {
                        color: 'rgba(255,255,255,0.85)',
                      }
                    }
                  }}
                  onClick={() => setExpandedGroups((prev) => ({ ...prev, [group.id]: !(prev[group.id] ?? true) }))}
                >
                  <ListItemIcon sx={{ minWidth: 28, transition: 'margin 0.2s' }}>
                    <Avatar
                      src={group.image ? `${backend.defaults.baseURL}/building-groups/${group.id}/image?municipalityId=${municipalityId}` : undefined}
                      sx={{ width: 24, height: 24, bgcolor: primaryColor, color: 'white', fontSize: '0.75rem', fontWeight: 'bold' }}
                    >
                      {!group.image && getBuildingInitials(group.name)}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={group.name}
                    secondary={`Objektů: ${buildingsInGroup.length}`}
                    primaryTypographyProps={{ sx: { fontWeight: 600, fontSize: '0.875rem' } }}
                    secondaryTypographyProps={{ sx: { fontSize: '0.75rem' } }}
                  />
                  {isExpanded ? <ExpandLessIcon className="sidebar-expand-icon" fontSize="small" /> : <ExpandMoreIcon className="sidebar-expand-icon" fontSize="small" />}
                </ListItemButton>
              </ListItem>
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                {buildingsInGroup.map((building) => (
                  <ListItemButtonSelected
                    key={building.id}
                    className="sidebar-item-level-3"
                    sx={{
                      py: 1,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                        '& .MuiChip-root': {
                          transform: 'scale(1.05)',
                          transition: 'transform 0.2s ease-in-out'
                        }
                      }
                    }}
                    component={Link}
                    urlLevel={3}
                    to={ROUTES.CLIENT_BUILDING_DETAILS.replace(":buildingId", building.id)}
                    isCollapsed={isCollapsed}
                  >
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <Avatar
                        src={building.image ? `${backend.defaults.baseURL}/municipalities/${building.municipalityId}/buildings/${building.id}/image` : undefined}
                        sx={{ width: 24, height: 24, bgcolor: primaryColor, color: 'white', fontSize: '0.75rem', fontWeight: 'bold' }}
                      >
                        {!building.image && getBuildingInitials(building.name)}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={building.name}
                      primaryTypographyProps={{
                        sx: { fontWeight: 500, fontSize: '0.9rem' }
                      }}
                    />
                    {dataQueueCount?.[building.id] && (
                      <Chip
                        className="sidebar-chip"
                        label={dataQueueCount[building.id].count}
                        size="small"
                        sx={{
                          backgroundColor: primaryColor,
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          height: 20,
                          '& .MuiChip-label': {
                            px: 1
                          }
                        }}
                      />
                    )}
                  </ListItemButtonSelected>
                ))}
              </Collapse>
            </Box>
          );
        })}

        {ungroupedBuildings.length > 0 && (
          <>
            <Divider sx={{ mx: 2, my: 0.5, opacity: 0.5 }} />
            <ListItem disablePadding sx={{ px: isCollapsed ? 0 : 1, py: 0.25 }}>
              <ListItemButton
                className="sidebar-item-level-2"
                sx={{
                  pr: 1,
                  borderRadius: '0 20px 20px 0',
                  '&:hover': {
                    backgroundColor: primaryColor,
                    color: 'white',
                    '& .MuiListItemIcon-root': {
                      color: 'white !important',
                    },
                    '& .MuiSvgIcon-root': {
                      color: 'white !important',
                    },
                    '& .MuiListItemText-primary': {
                      color: 'white',
                    }
                  }
                }}
                onClick={() => setUngroupedExpanded((prev) => !prev)}
              >
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <SidebarIcon component={IconAppartment} />
                </ListItemIcon>
                <ListItemText
                  primary="Nezařazené"
                  primaryTypographyProps={{ sx: { fontWeight: 600, fontSize: '0.875rem' } }}
                />
                {ungroupedExpanded ? <ExpandLessIcon className="sidebar-expand-icon" fontSize="small" /> : <ExpandMoreIcon className="sidebar-expand-icon" fontSize="small" />}
              </ListItemButton>
            </ListItem>
            <Collapse in={ungroupedExpanded} timeout="auto" unmountOnExit>
              {ungroupedBuildings.map((building) => (
                <ListItemButtonSelected
                  key={building.id}
                  className="sidebar-item-level-3"
                  sx={{
                    py: 1,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      '& .MuiChip-root': {
                        transform: 'scale(1.05)',
                        transition: 'transform 0.2s ease-in-out'
                      }
                    }
                  }}
                  component={Link}
                  urlLevel={3}
                  to={ROUTES.CLIENT_BUILDING_DETAILS.replace(":buildingId", building.id)}
                  isCollapsed={isCollapsed}
                >
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <Avatar
                      src={building.image ? `${backend.defaults.baseURL}/municipalities/${building.municipalityId}/buildings/${building.id}/image` : undefined}
                      sx={{ width: 24, height: 24, bgcolor: primaryColor, color: 'white', fontSize: '0.75rem', fontWeight: 'bold' }}
                    >
                      {!building.image && getBuildingInitials(building.name)}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={building.name}
                    primaryTypographyProps={{
                      sx: { fontWeight: 500, fontSize: '0.9rem' }
                    }}
                  />
                  {dataQueueCount?.[building.id] && (
                    <Chip
                      className="sidebar-chip"
                      label={dataQueueCount[building.id].count}
                      size="small"
                      sx={{
                        backgroundColor: primaryColor,
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 20,
                        '& .MuiChip-label': {
                          px: 1
                        }
                      }}
                    />
                  )}
                </ListItemButtonSelected>
              ))}
            </Collapse>
          </>
        )}
      </List>
    </>
  );
};

interface SidebarProps {
  isCollapsed?: boolean;
  onToggle?: () => void;
}

const Sidebar = ({ isCollapsed, onToggle }: SidebarProps) => {
  return (
    <Box sx={{
      width: isCollapsed ? 64 : 260,
      flexShrink: 0,
      transition: 'width 0.3s ease',
      position: 'relative',
      zIndex: 10,
    }}>
      <Paper
        elevation={isCollapsed ? 4 : 3}
        sx={{
          width: isCollapsed ? 64 : 260,
          boxSizing: "border-box",
          height: "100%",
          borderRadius: 0,
          background: `linear-gradient(180deg, 
            rgba(255, 255, 255, 1) 0%, 
            rgba(248, 250, 252, 1) 100%)`,
          borderRight: `1px solid rgba(0, 0, 0, 0.08)`,
          boxShadow: isCollapsed ? `4px 0px 12px rgba(0, 0, 0, 0.15)` : `
            0px 2px 8px rgba(0, 0, 0, 0.08),
            4px 0px 12px rgba(0, 0, 0, 0.04)
          `,
          paddingTop: "16px",
          paddingBottom: "16px",
          overflowX: "hidden",
          whiteSpace: "nowrap",
          transition: 'width 0.3s ease, box-shadow 0.3s ease',
          '& .sidebar-item-level-2': { paddingLeft: isCollapsed ? '16px !important' : '24px !important' },
          '& .sidebar-item-level-3': { paddingLeft: isCollapsed ? '16px !important' : '72px !important' },
          '& .MuiListItemText-root': { opacity: isCollapsed ? 0 : 1, transition: 'opacity 0.2s' },
          '& .sidebar-chip': { display: isCollapsed ? 'none' : 'flex' },
          '& .sidebar-expand-icon': { opacity: isCollapsed ? 0 : 1, transition: 'opacity 0.2s' },
          '&:hover': isCollapsed ? {
            width: 260,
            boxShadow: `8px 0px 24px rgba(0, 0, 0, 0.15)`,
            '& .sidebar-item-level-2': { paddingLeft: '24px !important' },
            '& .sidebar-item-level-3': { paddingLeft: '72px !important' },
            '& .MuiListItemText-root': { opacity: '1 !important' },
            '& .sidebar-chip': { display: 'flex !important' },
            '& .sidebar-expand-icon': { opacity: '1 !important' },
          } : {},
          '& .MuiListItemButton-root': {
            borderRadius: '0 24px 24px 0',
            margin: '2px 8px 2px 0',
            paddingLeft: '16px',
            paddingRight: '16px',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: primaryColor,
              transform: 'translateX(4px)',
              color: 'white',
              '& .MuiListItemIcon-root': {
                color: 'white !important',
              },
              '& .MuiSvgIcon-root': {
                color: 'white !important',
              },
            },
            '&.Mui-selected': {
              backgroundColor: primaryColor,
              color: 'white',
              '& .MuiListItemIcon-root': {
                color: 'white',
              },
              '& .MuiListItemText-primary': {
                color: 'white',
                fontWeight: 600,
              },
              '&:hover': {
                backgroundColor: primaryColor,
                transform: 'translateX(4px)',
                boxShadow: `0 4px 12px rgba(25, 118, 210, 0.3)`,
              }
            }
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', px: 1, mb: 1 }}>
          <IconButton onClick={onToggle} size="small" sx={{ color: 'text.secondary' }}>
            {isCollapsed ? <KeyboardDoubleArrowRightIcon /> : <KeyboardDoubleArrowLeftIcon />}
          </IconButton>
        </Box>
        <List sx={{ padding: 0 }}>
          <SidebarClientButtons isCollapsed={isCollapsed} />
        </List>
      </Paper>
    </Box>
  );
};

export default Sidebar;
